import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

interface PropertiesSectionProps {
  neighborhoodId: string;
}

// Property type definition to match estatebud API
interface Property {
  id: string;
  title?: {
    'en-US': string;
  };
  description?: {
    'en-US': string;
  };
  data?: {
    bedrooms?: string;
    bathrooms?: string;
  };
  street?: string;
  city?: string;
  zipcode?: string;
  images?: Array<{
    medium: string;
    caption: string;
    status: number;
  }>;
  floorplans?: Array<{
    medium: string;
    caption: string;
    status: number;
  }>;
  labels?: Array<{
    id: string;
    name: string;
    color: string;
    public: boolean;
  }>;
  agents?: Array<{
    firstname: string;
    lastname: string;
    title: string;
    email: string;
  }>;
}

const PropertiesSection: React.FC<PropertiesSectionProps> = ({ neighborhoodId }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProperties = async () => {
      if (neighborhoodId === 'highlands') {
        try {
          setLoading(true);
          // Use the local API wrapper instead of direct estatebud API call
          const response = await axios.get('/api/properties/highlands');
          
          // The API already filters for Rome properties, so no need to filter here
          setProperties(response.data);
          setError(null);
        } catch (err) {
          console.error('Error fetching properties:', err);
          setError('Failed to load properties. Please try again later.');
        } finally {
          setLoading(false);
        }
      } else if (neighborhoodId === 'castleberry') {
        try {
          setLoading(true);
          const response = await axios.get('/api/properties/castleberry');
          setProperties(response.data);
          setError(null);
        } catch (err) {
          console.error('Error fetching properties:', err);
          setError('Failed to load properties. Please try again later.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProperties();
  }, [neighborhoodId]);

  if (loading) {
    return (
      <div className="py-8">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-2 text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="py-8">
        <p className="text-gray-600 text-center">No properties available in this neighborhood at the moment.</p>
      </div>
    );
  }

  // Sort properties: available first, then sold/under contract
  const sortedProperties = [...properties].sort((a, b) => {
    const aIsSoldOrContract = a.labels?.some(label => ['SOLD', 'UNDER CONTRACT'].includes(label.name)) || false;
    const bIsSoldOrContract = b.labels?.some(label => ['SOLD', 'UNDER CONTRACT'].includes(label.name)) || false;
    
    if (aIsSoldOrContract && !bIsSoldOrContract) return 1;
    if (!aIsSoldOrContract && bIsSoldOrContract) return -1;
    return 0;
  });

  return (
    <div className="py-8">
      <h3 className="text-xl font-serif mb-6">Available Properties</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {sortedProperties.map(property => {
          const isSoldOrContract = property.labels?.some(label => ['SOLD', 'UNDER CONTRACT'].includes(label.name)) || false;
          
          return (
            <div key={property.id} className="bg-white rounded-lg shadow-md overflow-hidden group hover:shadow-xl transition duration-300">
              <Link to={`/property/${property.id}`} className="block">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={property.images && property.images.length > 0 ? property.images[0].medium : 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?q=80&w=640'} 
                    alt={property.title?.['en-US'] || 'Property'} 
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                  {isSoldOrContract && (
                    <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded text-sm font-semibold">
                      {property.labels?.find(label => label.name === 'SOLD') ? 'SOLD' : 'UNDER CONTRACT'}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition duration-300"></div>
                </div>
              </Link>
              
              <div className="p-6">
                <Link to={`/property/${property.id}`}>
                  <h4 className="text-xl font-serif mb-2 hover:text-primary transition">{property.title?.['en-US'] || `Property #${property.id}`}</h4>
                </Link>
                
                <div className="flex justify-between text-sm text-gray-600 mb-3">
                  {property.data?.bedrooms && <span>{property.data.bedrooms} Beds</span>}
                  {property.data?.bathrooms && <span>{property.data.bathrooms} Baths</span>}
                </div>
                
                {property.street && (
                  <div className="mb-3">
                    <span className="text-sm text-gray-600">
                      {property.street}{property.city ? `, ${property.city}` : ''}
                      {property.zipcode ? ` ${property.zipcode}` : ''}
                    </span>
                  </div>
                )}
                
                {property.labels && property.labels.some(label => label.name.startsWith('Move in')) && (
                  <div className="mb-3 text-sm text-green-600 font-medium">
                    {property.labels.find(label => label.name.startsWith('Move in'))?.name}
                  </div>
                )}
                
                {/* Floorplan model name display */}
                {property.labels && (
                  <div className="mb-4">
                    {property.labels.filter(label => 
                      ['Aurora', 'Victoria', 'Diana', 'Ceres', 'Pomona', 'Terra', 'Vesta', 'Cybele'].includes(label.name)
                    ).map(label => (
                      <span key={label.id} className="text-secondary font-medium">
                        {label.name} Floor Plan
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="mt-4">
                  <Link 
                    to={`/property/${property.id}`} 
                    className="btn btn-outline w-full text-center"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PropertiesSection; 